/**
 * Markdown + Mermaid renderer configuration
 *
 * Configures `marked` with GFM, heading IDs, mermaid code-block rendering
 * (via beautiful-mermaid) and ark-diagram iframes.
 */

import { marked, type Token } from 'marked'
import { renderMermaidSVG } from 'beautiful-mermaid'
import { WAVE_API } from '../config'

/**
 * Farben der Diagramme aus dem Stil der content-app statt fest
 * „github-light“, wie im Portal (cloud-v2 `agentMermaid.tsx`, Post #5447):
 * CSS-Variablen, die `.mermaid-rendered` in global.css aus den Theme-Token
 * setzt. Eigene Namen (`--mm-*`), weil das SVG selbst `--bg`, `--accent`,
 * `--surface`, `--border` setzt; `--accent: var(--accent)` wäre ein
 * Kreisbezug. Schrift nur als Familienname: beautiful-mermaid baut daraus
 * eine Google-Fonts-URL, ein Komma ergäbe dort 400.
 */
const mermaidColors = {
  bg: 'var(--mm-bg)',
  fg: 'var(--mm-fg)',
  line: 'var(--mm-linie)',
  accent: 'var(--mm-akzent)',
  muted: 'var(--mm-gedaempft)',
  surface: 'var(--mm-flaeche)',
  border: 'var(--mm-rand)',
  transparent: true,
  font: 'Google Sans Flex',
}


export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

marked.use({
  breaks: true,
  gfm: true,
  renderer: {
    // Ein Absatz, der nur aus einer fetten Zeile besteht, ist eine
    // Zwischenzeile ("**1. Das Abo gehört …**"). Er bekommt eine Klasse,
    // damit der Abstand davor größer ist als der zum folgenden Block.
    paragraph(this: { parser: { parseInline: (t: Token[]) => string } }, { tokens }: { tokens: Token[] }) {
      const lead = tokens.length === 1 && tokens[0].type === 'strong'
      return `<p${lead ? ' class="md-lead"' : ''}>${this.parser.parseInline(tokens)}</p>\n`
    },
    heading({ text, depth }: { text: string; depth: number }) {
      const id = slugify(text)
      return `<h${depth} id="${id}">${text}</h${depth}>`
    },
    code({ text, lang }: { text: string; lang?: string | undefined }) {
      if (lang === 'mermaid') {
        const decoded = text
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
        // beautiful-mermaid zeichnet flowchart/graph, sequence, state, class,
        // ER und xychart-beta samt linkStyle im Stil der content-app. Nur
        // Arten, die es nicht kennt (gantt, pie, mindmap, timeline, journey …),
        // melden „Invalid mermaid header“; die zeichnet das klassische mermaid
        // nach dem Einhängen (useMermaidAsync), im einfachen Stil. Jeder
        // andere Fehler zeigt den Quelltext.
        try {
          const svg = renderMermaidSVG(decoded, mermaidColors)
          // Unlesbare Eingaben quittiert beautiful-mermaid nicht mit einem
          // Fehler, sondern mit einem leeren Bild (Fläche 0 × 0). Dann wie
          // bei einem Fehler den Quelltext zeigen statt einer Lücke.
          if (/viewBox="0 0 0 0"/.test(svg)) throw new Error('leeres Diagramm')
          return `<div class="mermaid-rendered">${svg}</div>`
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e)
          if (/Invalid mermaid header/i.test(msg)) {
            return `<div class="mermaid-async mermaid-rendered" data-mermaid-source="${escapeHtml(decoded)}"></div>`
          }
          return `<pre class="mermaid-error" title="${escapeHtml(`mermaid: ${msg}`)}"><code>${escapeHtml(decoded)}</code></pre>`
        }
      }
      // D2 (d2lang.com) — state-of-art text-to-diagram language with
      // ELK auto-layout. Same async-placeholder pattern as the mermaid
      // fallback — `useD2Async` post-processes after mount.
      if (lang === 'd2') {
        const decoded = text
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
        const escaped = decoded
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
        return `<div class="d2-async" data-d2-source="${escaped}"></div>`
      }
      if (lang === 'ark') {
        const decoded = text
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
        const config = btoa(unescape(encodeURIComponent(decoded)))
        const instanceId = Math.random().toString(36).slice(2, 8)
        if (!WAVE_API) {
          return `<div class="ark-diagram ark-diagram--unavailable"><em>Diagramm-Einbettung ist auf dieser Instanz nicht konfiguriert (VITE_WAVE_API).</em></div>`
        }
        return `<div class="ark-diagram"><iframe src="${WAVE_API}/ark?embed=true&instance=${instanceId}&config=${config}" width="100%" height="500" frameborder="0" allowfullscreen loading="lazy"></iframe></div>`
      }
      return false as unknown as string
    },
  },
})

// ---------------------------------------------------------------------------
// iA-Writer-Konventionen in der Leseansicht
// ---------------------------------------------------------------------------
// Beiträge aus iA Writer tragen zwei Elemente, die kein Markdown sind:
//   {{TOC}}  auf einer eigenen Zeile: Inhaltsverzeichnis
//   +++      auf einer eigenen Zeile: Seitenumbruch
// Der PDF-Export versteht beide (markdown-api, Themes doku, drone-challenge,
// buch-kunst). Die Leseansicht zeigte sie bis 04.10.2026 wörtlich: bei
// Beitrag #5226 stand „{{TOC}}" im Text und 26-mal ein Absatz „+++".

const FENCE_RE = /^\s{0,3}(`{3,}|~{3,})/
const PAGEBREAK_RE = /^\+\+\+\s*$/
const TOC_RE = /^\{\{TOC\}\}\s*$/
const HEADING_RE = /^(#{1,2})\s+(.+?)\s*#*\s*$/

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** Zeilen außerhalb von Codeblöcken, mit Index. */
function proseLines(lines: string[]): boolean[] {
  const prose: boolean[] = []
  let fence = ''
  for (const line of lines) {
    const m = FENCE_RE.exec(line)
    if (fence) {
      prose.push(false)
      if (m && m[1][0] === fence[0] && m[1].length >= fence.length) fence = ''
    } else if (m) {
      fence = m[1]
      prose.push(false)
    } else {
      prose.push(true)
    }
  }
  return prose
}

/** Inhaltsverzeichnis aus den Überschriften erster und zweiter Ebene. */
function buildToc(source: string): string {
  const lines = source.split('\n')
  const prose = proseLines(lines)
  const items: string[] = []
  let open = false
  for (let i = 0; i < lines.length; i++) {
    if (!prose[i]) continue
    const m = HEADING_RE.exec(lines[i])
    if (!m) continue
    const raw = m[2]
    const label = escapeHtml(raw.replace(/[*_`]/g, '').replace(/\[([^\]]+)\]\([^)]*\)/g, '$1'))
    const link = `<a href="#${slugify(raw)}">${label}</a>`
    if (m[1].length === 1) {
      if (open) items.push('</ul></li>')
      items.push(`<li class="ia-toc__part">${link}<ul>`)
      open = true
    } else {
      items.push(`<li>${link}</li>`)
    }
  }
  if (open) items.push('</ul></li>')
  if (items.length === 0) return ''
  return `<nav class="ia-toc" aria-label="Inhalt"><ul>${items.join('')}</ul></nav>`
}

/**
 * Ersetzt `{{TOC}}` und `+++` durch HTML-Blöcke. `tocSource` ist das ganze
 * Dokument, wenn `content` nur ein Ausschnitt davon ist (Collab-Ansicht
 * rendert absatzweise); sonst wird das Verzeichnis aus `content` gebaut.
 */
export function applyIaConventions(content: string, tocSource?: string): string {
  if (!content.includes('+++') && !content.includes('{{TOC}}')) return content
  const lines = content.split('\n')
  const prose = proseLines(lines)
  const out: string[] = []
  for (let i = 0; i < lines.length; i++) {
    if (prose[i] && PAGEBREAK_RE.test(lines[i])) {
      // Leerzeilen davor und danach: Ein „+++" direkt unter einer
      // Überschrift soll nicht Teil der Überschrift werden.
      out.push('', '<div class="ia-pagebreak" role="separator" aria-label="Seitenumbruch"></div>', '')
    } else if (prose[i] && TOC_RE.test(lines[i])) {
      out.push('', buildToc(tocSource ?? content), '')
    } else {
      out.push(lines[i])
    }
  }
  return out.join('\n')
}

export function parseMarkdown(content: string, tocSource?: string): string {
  return marked(applyIaConventions(content, tocSource)) as string
}
